/**
 * Sources API - Complete implementation matching trace-api.alpyne.ai/docs
 */

import { DatabaseSource, SyncStatusResponse, Chunk } from '../types/api';
import { getAPIClient } from './client';

// Register a new source
export async function registerSource(data: {
  project_id: string;
  type: 'postgres' | 'mysql' | 'mssql';
  description?: string;
  connection_config: Record<string, any>;
  sync_config?: Record<string, any>;
  source_region?: string;
}): Promise<DatabaseSource> {
  const client = getAPIClient();
  return client.post<DatabaseSource>('/sources', data);
}

// Register database source specifically
export async function registerDatabaseSource(data: {
  project_id: string;
  source_type: 'postgres' | 'mysql' | 'mssql';
  uri: string;
  description?: string;
  connection_config: Record<string, any>;
  sync_config: Record<string, any>;
  source_region: string;
}): Promise<DatabaseSource> {
  const client = getAPIClient();
  return client.post<DatabaseSource>('/sources/database', data);
}

// Register local folder source
export async function registerLocalFolderSource(data: {
  project_id: string;
  name: string;
  file_path: string;
  description?: string;
  watch_for_changes?: boolean;
  recursive?: boolean;
  file_extensions?: string[];
  exclude_patterns?: string[];
  // Optional file statistics from frontend scan
  files?: Array<{
    name: string;
    path: string;
    size: number;
    extension: string;
    rows?: number;
    columns?: number;
  }>;
  file_count?: number;
  total_size_bytes?: number;
  files_by_type?: Record<string, number>;
  // Connection configuration for SSH/UNC (Keystone only)
  connection_config?: {
    type: 'ssh' | 'unc' | 'local';
    host?: string;
    port?: number;
    username?: string;
    password?: string;
    key_path?: string;
    remote_path?: string;
    unc_path?: string;
  };
}): Promise<any> {
  const client = getAPIClient();
  return client.post('/sources/local-folder', data);
}

// Register SharePoint source
export async function registerSharePointSource(data: {
  project_id: string;
  name: string;
  description?: string;
  credentials: {
    tenant_id: string;
    client_id: string;
    client_secret: string;
    site_url: string;
  };
  library_path?: string;
  watch_for_changes?: boolean;
  recursive?: boolean;
  file_extensions?: string[];
  sync_frequency?: 'hourly' | '6hourly' | 'daily';
}): Promise<any> {
  const client = getAPIClient();
  return client.post('/sources/sharepoint', data);
}

// Register Google Drive source
export async function registerGDriveSource(data: {
  project_id: string;
  name: string;
  description?: string;
  credentials: {
    service_account_json: string;
    shared_drive_id?: string;
  };
  folder_id?: string;
  watch_for_changes?: boolean;
  recursive?: boolean;
  file_extensions?: string[];
  sync_frequency?: 'hourly' | '6hourly' | 'daily';
  include_shared_with_me?: boolean;
}): Promise<any> {
  const client = getAPIClient();
  return client.post('/sources/gdrive', data);
}

// Register API Integration source
export async function registerAPIIntegrationSource(data: {
  project_id: string;
  name: string;
  description?: string;
  integration_type: 'canada_grants' | 'canada_tenders' | 'samgov' | 'worldbank' | 'usa_awards' | 'usa_budgets' | 'federal_register' | 'executive_orders' | 'rss_feeds' | 'bc_bid' | 'bids_tender' | 'bonfire' | 'merx' | 'sap_ariba' | 'jaggaer' | 'city_portals';
  api_config?: Record<string, any>;
  filters?: Record<string, any>;
  sync_frequency?: 'hourly' | 'daily' | 'weekly';
  enabled?: boolean;
}): Promise<any> {
  const client = getAPIClient();
  return client.post('/sources/api-integration', data);
}

// List sources
export async function listSources(
  projectId: string, 
  options?: {
    artifact_ids_limit?: number;  // Max artifact IDs to return per source (default 50)
    include_all_artifact_ids?: boolean;  // Include all artifact IDs (use with caution)
  }
): Promise<DatabaseSource[]> {
  const client = getAPIClient();
  return client.get<DatabaseSource[]>('/sources', { 
    params: { 
      project_id: projectId,
      artifact_ids_limit: options?.artifact_ids_limit ?? 50,
      include_all_artifact_ids: options?.include_all_artifact_ids ?? false
    } 
  });
}

// Get source details
export async function getSource(sourceId: string): Promise<DatabaseSource> {
  const client = getAPIClient();
  return client.get<DatabaseSource>(`/sources/${sourceId}`);
}

// Delete source
export async function deleteSource(sourceId: string): Promise<void> {
  const client = getAPIClient();
  return client.delete(`/sources/${sourceId}`);
}

// Trigger database sync
export async function triggerSync(sourceId: string, tableName?: string): Promise<{ job_id: string; tables_queued: number }> {
  const client = getAPIClient();
  return client.post(`/sources/${sourceId}/sync`, tableName ? { table_name: tableName } : {});
}

// Get sync status
export async function getSyncStatus(sourceId: string): Promise<SyncStatusResponse> {
  const client = getAPIClient();
  return client.get<SyncStatusResponse>(`/sources/${sourceId}/sync-status`);
}

// Preview API integration sync (estimate count before syncing)
export async function previewSyncAPIIntegration(
  sourceId: string, 
  filters?: Record<string, any>
): Promise<{
  estimated_count: number;
  filters: Record<string, any>;
  last_sync: {
    timestamp: string | null;
    item_count: number | null;
    duration_seconds: number | null;
    job_id: string | null;
  } | null;
  integration_type: string;
  source_name: string;
}> {
  const client = getAPIClient();
  return client.post(`/sources/${sourceId}/sync-preview`, { filters: filters || {} });
}

// Trigger file upload from local folder source
export async function triggerFileUpload(sourceId: string, asyncScan: boolean = true): Promise<{
  source_id: string;
  job_id: string;
  files_found: number;
  files_queued: number;
  message: string;
}> {
  const client = getAPIClient();
  return client.post(`/sources/${sourceId}/upload?async_scan=${asyncScan}`);
}

// Get chunks from all artifacts in a source
export async function getSourceChunks(
  sourceId: string,
  filters?: {
    contains_table?: boolean;
    contains_figure?: boolean;
  }
): Promise<Chunk[]> {
  const client = getAPIClient();
  const params: Record<string, string> = {};
  if (filters?.contains_table !== undefined) {
    params.contains_table = String(filters.contains_table);
  }
  if (filters?.contains_figure !== undefined) {
    params.contains_figure = String(filters.contains_figure);
  }
  return client.get<Chunk[]>(`/sources/${sourceId}/chunks`, { params });
}

// Get entity count for a source
export async function getSourceEntityCount(sourceId: string): Promise<{
  entity_count: number;
  entity_types: Record<string, number>;
  artifact_count: number;
}> {
  const client = getAPIClient();
  return client.get(`/sources/${sourceId}/entity-count`);
}

// Update source metadata (health status, metrics, sync info)
export async function updateSourceMetadata(
  sourceId: string,
  data: {
    health_status?: 'healthy' | 'degraded' | 'error' | 'disabled';
    health_message?: string;
    response_time_ms?: number;
    last_sync_at?: string;
    item_count?: number;
    metrics?: Record<string, any>;
    enabled?: boolean;
  }
): Promise<{
  source_id: string;
  project_id: string;
  metadata: Record<string, any>;
  sync_status: string | null;
  last_sync_at: string | null;
  updated_at: string;
}> {
  const client = getAPIClient();
  // Backend expects metadata to be wrapped in a metadata object
  return client.patch(`/sources/${sourceId}/metadata`, { metadata: data });
}
